import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Trash2, X, Pencil, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { itemPricesService } from '@/services/inventory';
import type { ItemPrice } from '@/services/inventory';
import type { InventoryItem } from '@/types/inventory';

// Curated ISO 4217 list, biased toward currencies most likely on a
// global SMB price book. Codes paired with a display label.
const CURRENCY_OPTIONS: { code: string; label: string }[] = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'INR', label: 'INR — Indian Rupee' },
  { code: 'AED', label: 'AED — UAE Dirham' },
  { code: 'SAR', label: 'SAR — Saudi Riyal' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'CNY', label: 'CNY — Chinese Yuan' },
  { code: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar' },
  { code: 'KRW', label: 'KRW — South Korean Won' },
  { code: 'MYR', label: 'MYR — Malaysian Ringgit' },
  { code: 'THB', label: 'THB — Thai Baht' },
  { code: 'IDR', label: 'IDR — Indonesian Rupiah' },
  { code: 'PHP', label: 'PHP — Philippine Peso' },
  { code: 'VND', label: 'VND — Vietnamese Dong' },
  { code: 'ZAR', label: 'ZAR — South African Rand' },
  { code: 'MXN', label: 'MXN — Mexican Peso' },
  { code: 'BRL', label: 'BRL — Brazilian Real' },
  { code: 'NGN', label: 'NGN — Nigerian Naira' },
  { code: 'KES', label: 'KES — Kenyan Shilling' },
  { code: 'TRY', label: 'TRY — Turkish Lira' },
  { code: 'SEK', label: 'SEK — Swedish Krona' },
  { code: 'NOK', label: 'NOK — Norwegian Krone' },
  { code: 'DKK', label: 'DKK — Danish Krone' },
  { code: 'PLN', label: 'PLN — Polish Złoty' },
];

interface Props {
  item: InventoryItem | null;
  /**
   * When true, the pinned base-currency row is not rendered (caller is
   * already showing it as editable form fields above this section).
   * Used by ItemFormSheet's Sale Prices tab where the form fields own
   * the base price/description.
   */
  hideBaseRow?: boolean;
}

/**
 * Per-item additional currency prices. The tenant's base-currency
 * price stays on the item form (InventoryItem.sellingPrice). This
 * section is where the operator adds prices in other currencies
 * (e.g. USD on an INR tenant for export orders).
 */
export function ItemPricesSection({ item, hideBaseRow = false }: Props) {
  const qc = useQueryClient();
  const me = useCurrentUser();
  const baseCurrency = me?.tenantCurrency ?? 'INR';
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<ItemPrice | null>(null);

  const itemId = item?.id ?? null;

  const pricesQuery = useQuery({
    queryKey: ['inventory', 'item-prices', itemId],
    queryFn: () => itemPricesService.list(itemId!),
    enabled: !!itemId,
  });

  const remove = useMutation({
    mutationFn: (priceId: string) => itemPricesService.remove(itemId!, priceId),
    onSuccess: () => {
      toast.success('Price removed');
      qc.invalidateQueries({ queryKey: ['inventory', 'item-prices', itemId] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not delete price'),
  });

  const prices = pricesQuery.data ?? [];
  // Currencies already in use on this item — passed to PriceForm so its
  // dropdown excludes them (the BE rejects duplicates anyway, but
  // filtering upfront is better UX). The base currency is also
  // excluded (handled inside PriceForm via the baseCurrency prop).
  const usedCurrencies = useMemo(
    () => new Set(prices.map((p) => p.currency.toUpperCase())),
    [prices],
  );

  if (!item) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-muted-foreground">
          {hideBaseRow ? (
            <>Add rows for currencies other than {baseCurrency}.</>
          ) : (
            <>
              Base currency: <span className="font-mono text-foreground">{baseCurrency}</span>
              <span className="mx-1.5">·</span>
              set the base price on the Sale Prices tab; add other currencies here.
            </>
          )}
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setCreating(true)}
        >
          <Plus className="h-3 w-3" />
          Add price
        </Button>
      </div>

      {creating && (
        <PriceForm
          itemId={item.id}
          baseCurrency={baseCurrency}
          usedCurrencies={usedCurrencies}
          onClose={() => setCreating(false)}
        />
      )}

      <div className="overflow-hidden rounded-md border border-border/70">
        <div className="grid grid-cols-12 gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          <div className="col-span-2">Currency</div>
          <div className="col-span-3 text-right">Selling price</div>
          <div className="col-span-3 text-right">Cost price</div>
          <div className="col-span-3">Notes</div>
          <div className="col-span-1" />
        </div>

        {/* Pinned base-currency row (read-only — base price lives elsewhere) */}
        {!hideBaseRow && (
          <div className="grid grid-cols-12 items-center gap-2 border-b border-border/60 bg-primary/[0.03] px-3 py-2 text-[12.5px]">
            <div className="col-span-2">
              <Badge variant="secondary" className="font-mono">
                {baseCurrency}
              </Badge>
              <span className="ml-1.5 text-[10px] text-muted-foreground">base</span>
            </div>
            <div className="col-span-3 text-right font-mono text-muted-foreground">
              {item.sellingPrice != null ? Number(item.sellingPrice).toFixed(2) : '—'}
            </div>
            <div className="col-span-3 text-right font-mono text-muted-foreground">
              {item.costPrice != null ? Number(item.costPrice).toFixed(2) : '—'}
            </div>
            <div className="col-span-3 text-[11px] text-muted-foreground">
              Edit on the Sale Prices tab
            </div>
            <div className="col-span-1" />
          </div>
        )}

        {prices.length === 0 && !creating ? (
          <div className="px-3 py-5 text-center text-[12px] text-muted-foreground">
            No additional currency prices yet. Add one above.
          </div>
        ) : (
          prices.map((price) =>
            editingId === price.id ? (
              <PriceForm
                key={price.id}
                itemId={item.id}
                baseCurrency={baseCurrency}
                usedCurrencies={usedCurrencies}
                existing={price}
                onClose={() => setEditingId(null)}
              />
            ) : (
              <PriceRow
                key={price.id}
                price={price}
                onEdit={() => setEditingId(price.id)}
                onRemove={() => setConfirmRemove(price)}
              />
            ),
          )
        )}
      </div>
      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={confirmRemove ? `Remove the ${confirmRemove.currency} price?` : ''}
        description={
          <>
            This will delete the {confirmRemove?.currency} row from this item.
            Existing orders priced in that currency are unaffected.
          </>
        }
        confirmLabel="Remove price"
        onConfirm={() => {
          if (confirmRemove) void remove.mutateAsync(confirmRemove.id);
        }}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────

function PriceRow({
  price,
  onEdit,
  onRemove,
}: {
  price: ItemPrice;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <div className="grid grid-cols-12 items-center gap-2 border-b border-border/40 px-3 py-2 text-[12.5px] last:border-b-0">
      <div className="col-span-2 font-mono">{price.currency}</div>
      <div className="col-span-3 text-right font-mono">
        {Number(price.sellingPrice).toFixed(2)}
      </div>
      <div className="col-span-3 text-right font-mono">
        {price.costPrice != null ? Number(price.costPrice).toFixed(2) : '—'}
      </div>
      <div className="col-span-3 truncate text-[11.5px] text-muted-foreground">
        {price.notes ?? ''}
      </div>
      <div className="col-span-1 flex justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onEdit}
          aria-label="Edit price"
        >
          <Pencil className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          aria-label="Remove price"
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────

function PriceForm({
  itemId,
  baseCurrency,
  usedCurrencies,
  existing,
  onClose,
}: {
  itemId: string;
  baseCurrency: string;
  usedCurrencies: Set<string>;
  existing?: ItemPrice;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!existing;
  // The dropdown shows "USD" as a placeholder; if we leave the state
  // empty the operator can save without ever opening the dropdown and
  // the BE rejects with "currency must be a 3-letter ISO code". Seed
  // with the first eligible option so the visible default is a real
  // selected value, not just a placeholder.
  const eligibleCurrencies = useMemo(
    () =>
      CURRENCY_OPTIONS.filter(
        (opt) =>
          opt.code !== baseCurrency.toUpperCase() && !usedCurrencies.has(opt.code),
      ),
    [baseCurrency, usedCurrencies],
  );
  const [currency, setCurrency] = useState(
    existing?.currency ?? eligibleCurrencies[0]?.code ?? '',
  );
  const [sellingPrice, setSellingPrice] = useState<string>(
    existing?.sellingPrice != null ? String(existing.sellingPrice) : '',
  );
  const [costPrice, setCostPrice] = useState<string>(
    existing?.costPrice != null ? String(existing.costPrice) : '',
  );
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');

  const save = useMutation({
    mutationFn: async () => {
      const sp = Number(sellingPrice);
      const cp = costPrice === '' ? undefined : Number(costPrice);
      if (Number.isNaN(sp) || sp < 0) {
        throw new Error('Selling price must be a non-negative number');
      }
      if (cp !== undefined && (Number.isNaN(cp) || cp < 0)) {
        throw new Error('Cost price must be a non-negative number');
      }
      if (isEdit && existing) {
        return itemPricesService.update(itemId, existing.id, {
          sellingPrice: sp,
          costPrice: cp,
          notes: notes.trim() || undefined,
        });
      }
      const cur = currency.toUpperCase().trim();
      if (!/^[A-Z]{3}$/.test(cur)) {
        throw new Error('Currency must be a 3-letter ISO code (e.g. USD, EUR, AED)');
      }
      if (cur === baseCurrency.toUpperCase()) {
        throw new Error(
          `${cur} is the tenant's base currency. Edit it on the Pricing tab instead.`,
        );
      }
      return itemPricesService.create(itemId, {
        currency: cur,
        sellingPrice: sp,
        costPrice: cp,
        notes: notes.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Price updated' : 'Price added');
      qc.invalidateQueries({ queryKey: ['inventory', 'item-prices', itemId] });
      onClose();
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not save price'),
  });

  return (
    <div
      className="grid grid-cols-12 items-center gap-2 border-b border-border/40 bg-muted/20 px-3 py-2 text-[12.5px]"
      // Stop Enter from bubbling up and submitting the surrounding
      // item form. Enter inside any input triggers the inline save.
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          if (!save.isPending) save.mutate();
        }
      }}
    >
      <div className="col-span-2">
        {isEdit ? (
          // Currency is the row's key — can't change on edit.
          <div className="flex h-8 items-center font-mono text-[12.5px]">{currency}</div>
        ) : (
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="h-8 font-mono">
              <SelectValue placeholder="Pick currency" />
            </SelectTrigger>
            <SelectContent>
              {eligibleCurrencies.map((opt) => (
                <SelectItem key={opt.code} value={opt.code}>
                  <span className="font-mono text-[12px]">{opt.code}</span>
                  <span className="ml-2 text-[11px] text-muted-foreground">
                    {opt.label.split('—')[1]?.trim()}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="col-span-3">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={sellingPrice}
          onChange={(e) => setSellingPrice(e.target.value)}
          placeholder="0.00"
          className="h-8 text-right font-mono"
          aria-label="Selling price"
        />
      </div>
      <div className="col-span-3">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={costPrice}
          onChange={(e) => setCostPrice(e.target.value)}
          placeholder="optional"
          className="h-8 text-right font-mono"
          aria-label="Cost price"
        />
      </div>
      <div className="col-span-3">
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Export — DDP"
          maxLength={500}
          className="h-8"
          aria-label="Notes"
        />
      </div>
      <div className="col-span-1 flex justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          aria-label="Save"
          className="text-emerald-600 hover:text-emerald-700"
        >
          <Check className="h-3 w-3" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          aria-label="Cancel"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}
